import { Observation, QuestionnaireResponse } from "@medplum/fhirtypes";

import {
  isObservationPartOfEncounterAmendmentDiff,
  isObservationWithAmendVersionChain,
} from "@/encounters/components/EncounterLabor/services/labor-encounter-amend-observation-filter";
import { getQuestionnaireResponseIdFromObservationDerivedFrom } from "@/utils/medplum";

export interface LaborStageMemberRowVersionDiffInclusionOptions {
  stageMoveMemberIds: ReadonlySet<string>;
  provenanceTargetObservationIds?: ReadonlySet<string>;
  provenanceTargetFlowsheetQuestionnaireResponseIds?: ReadonlySet<string>;
  restrictToEncounterAmendmentWindow: boolean;
  showOnlyObservationVersionChainDiffs: boolean;
  previousEncounterQuestionnaireResponse?: QuestionnaireResponse;
  currentEncounterQuestionnaireResponse?: QuestionnaireResponse;
  observationsById: ReadonlyMap<string, Observation>;
  questionnaireResponsesById?: ReadonlyMap<string, QuestionnaireResponse>;
}

/**
 * Whether a labor stage member observation should be considered for version diff rendering.
 */
export function shouldIncludeLaborStageMemberRowInVersionDiff(
  currentObservation: Observation,
  options: LaborStageMemberRowVersionDiffInclusionOptions,
): boolean {
  if (currentObservation.id && options.stageMoveMemberIds.has(currentObservation.id)) {
    return false;
  }

  const flowsheetQuestionnaireResponseId = getQuestionnaireResponseIdFromObservationDerivedFrom(
    currentObservation.derivedFrom,
  );
  if (
    flowsheetQuestionnaireResponseId &&
    options.provenanceTargetFlowsheetQuestionnaireResponseIds?.has(flowsheetQuestionnaireResponseId)
  ) {
    return true;
  }

  if (
    options.provenanceTargetObservationIds &&
    options.provenanceTargetObservationIds.size > 0 &&
    currentObservation.id &&
    options.provenanceTargetObservationIds.has(currentObservation.id)
  ) {
    return true;
  }

  const hasExplicitProvenanceObservationTargets = Boolean(
    options.provenanceTargetObservationIds?.size,
  );

  if (
    hasExplicitProvenanceObservationTargets &&
    currentObservation.id &&
    !options.restrictToEncounterAmendmentWindow &&
    !options.showOnlyObservationVersionChainDiffs
  ) {
    return false;
  }

  if (options.showOnlyObservationVersionChainDiffs) {
    return isObservationWithAmendVersionChain(currentObservation, options.observationsById);
  }

  if (!options.restrictToEncounterAmendmentWindow) {
    return true;
  }

  if (
    !options.previousEncounterQuestionnaireResponse ||
    !options.currentEncounterQuestionnaireResponse
  ) {
    return false;
  }

  return isObservationPartOfEncounterAmendmentDiff(
    currentObservation,
    options.previousEncounterQuestionnaireResponse,
    options.currentEncounterQuestionnaireResponse,
    {
      observationsById: options.observationsById,
      questionnaireResponsesById: options.questionnaireResponsesById,
    },
  );
}
